import { useEffect, useRef, useState } from 'react';
import { Animated, Pressable, StyleSheet, Text, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { colors, fontFamily, space, type } from '~/ui/tokens';
import { useReducedMotion } from '~/hooks/useReducedMotion';

export interface AccordionEntry {
  question: string;
  answer: string;
}

interface AccordionProps {
  items: AccordionEntry[];
  /** Controlled open question (by `question` text, stable across lists that
   * get split into several `Accordion`s — see the homepage FAQ columns, which
   * must share one open question rather than each keeping its own index).
   * Omit both this and `onToggle` for a standalone, self-contained accordion. */
  openQuestion?: string | null;
  onToggle?: (question: string) => void;
}

export function Accordion({ items, openQuestion, onToggle }: AccordionProps) {
  const [internalOpen, setInternalOpen] = useState<string | null>(null);
  const open = onToggle ? openQuestion ?? null : internalOpen;
  const toggle = onToggle ?? ((question: string) => setInternalOpen((current) => (current === question ? null : question)));

  return (
    <View>
      {items.map((item) => (
        <AccordionRow key={item.question} item={item} isOpen={open === item.question} onToggle={() => toggle(item.question)} />
      ))}
    </View>
  );
}

function AccordionRow({ item, isOpen, onToggle }: { item: AccordionEntry; isOpen: boolean; onToggle: () => void }) {
  const reduceMotion = useReducedMotion();
  const progress = useRef(new Animated.Value(isOpen ? 1 : 0)).current;
  const [measuredHeight, setMeasuredHeight] = useState<number | null>(null);

  useEffect(() => {
    if (reduceMotion) {
      progress.setValue(isOpen ? 1 : 0);
      return;
    }
    Animated.spring(progress, {
      toValue: isOpen ? 1 : 0,
      friction: 9,
      tension: 55,
      useNativeDriver: false,
    }).start();
  }, [isOpen, progress, reduceMotion]);

  const rotate = progress.interpolate({ inputRange: [0, 1], outputRange: ['0deg', '180deg'] });

  return (
    <View style={styles.item}>
      <Pressable
        accessibilityRole="button"
        accessibilityState={{ expanded: isOpen }}
        style={styles.header}
        onPress={onToggle}
      >
        <Text style={styles.question}>{item.question}</Text>
        <Animated.View style={{ transform: [{ rotate }] }}>
          <Ionicons name="chevron-down" size={18} color={colors.inkFaint} />
        </Animated.View>
      </Pressable>
      <Animated.View
        style={[
          styles.answerClip,
          { opacity: progress },
          measuredHeight != null ? { height: progress.interpolate({ inputRange: [0, 1], outputRange: [0, measuredHeight] }) } : null,
        ]}
      >
        {/* onLayout measures this wrapper, not the Text directly — the Text's
            own marginTop (the gap under the question) isn't part of its own
            layout box, so measuring the Text alone under-reports the height
            needed by exactly that margin and the animated clip below cuts
            that amount off the bottom of the last line. */}
        <View onLayout={(event) => {
          const height = event.nativeEvent.layout.height;
          setMeasuredHeight((current) => (current === height ? current : height));
        }}>
          <Text style={styles.answer}>{item.answer}</Text>
        </View>
      </Animated.View>
    </View>
  );
}

const styles = StyleSheet.create({
  item: {
    borderBottomWidth: 1,
    borderBottomColor: colors.lineSoft,
    paddingVertical: space.internal,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: space.internal,
  },
  question: {
    flex: 1,
    fontFamily: fontFamily.semibold,
    ...type.bodyLarge,
    color: colors.ink,
  },
  answerClip: {
    overflow: 'hidden',
  },
  answer: {
    fontFamily: fontFamily.regular,
    ...type.bodyRegular,
    color: colors.inkSoft,
    marginTop: space.related,
    maxWidth: 720,
  },
});
